import React, { useState } from "react";

// Matches the Subscription page's actual button/badge visual language
// (SubscriptionDashboard.jsx's "+ Create New Plan" pill button and
// SubscriptionTable.jsx's tinted status pills) rather than the app's other
// var(--button-color) convention - Subscription hardcodes its own
// magenta/crimson (#C0335A), so this mirrors that exactly for the
// Estimations flow that now sits alongside it in the Scheduling nav group.
const TINTS = {
  gray: { bg: "#F3F4F6", color: "#374151", border: "#D1D5DB" },
  blue: { bg: "#EFF6FF", color: "#2563EB", border: "#BFDBFE" },
  green: { bg: "#E6F9F0", color: "#27AE60", border: "#27AE60" },
  red: { bg: "#FFF3F3", color: "#C0335A", border: "#C0335A" },
  orange: { bg: "#FFF7ED", color: "#C2410C", border: "#FDBA74" },
  purple: { bg: "#F5F3FF", color: "#7C3AED", border: "#DDD6FE" },
};

export function PillButton({ children, disabled, style, ...rest }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      disabled={disabled}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: disabled ? "#e89fb7" : hover ? "#a52b4e" : "#C0335A",
        color: "#fff",
        border: "none",
        borderRadius: "9999px",
        padding: "10px 22px",
        fontSize: "14px",
        fontWeight: 600,
        cursor: disabled ? "not-allowed" : "pointer",
        transition: "background 0.2s",
        ...style,
      }}
      {...rest}
    >
      {children}
    </button>
  );
}

export function TintButton({ children, tint = "gray", disabled, style, ...rest }) {
  const t = TINTS[tint] || TINTS.gray;
  return (
    <button
      type="button"
      disabled={disabled}
      style={{
        background: t.bg,
        color: t.color,
        border: "none",
        borderRadius: "8px",
        padding: "8px 16px",
        fontSize: "13px",
        fontWeight: 600,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.6 : 1,
        textDecoration: "none",
        display: "inline-block",
        ...style,
      }}
      {...rest}
    >
      {children}
    </button>
  );
}

export const STATUS_TINT = {
  DRAFT: "gray",
  SENT: "blue",
  ACCEPTED: "green",
  DECLINED: "red",
  EXPIRED: "orange",
  CONVERTED: "purple",
};

// Matches Inventory's KPICard look (src/components/Analytics/KPICard.jsx /
// src/components/InventoryAnalytics/InventoryDashboardWidget.jsx) - white
// card, thin neutral border, no shadow, label+icon row above a bold value -
// rather than Subscription's pink-branded stat tiles, per product direction.
// Doubles as a clickable filter tile (list page) or a plain display tile
// (detail page) depending on whether `onClick` is passed; KPICard itself has
// no active/selected state since it's purely informational there, so this
// adds one (tinted border + faint background) for the filter-tile usage.
export function StatTile({ label, value, icon: Icon, tint = "gray", active, onClick }) {
  const t = TINTS[tint] || TINTS.gray;
  const clickable = typeof onClick === "function";
  return (
    <div
      onClick={onClick}
      style={{
        background: active ? t.bg : "#fff",
        border: active ? `2px solid ${t.border}` : "1px solid #E5E7EB",
        borderRadius: "8px",
        padding: "20px",
        display: "flex",
        flexDirection: "column",
        gap: "12px",
        flex: "1 1 160px",
        cursor: clickable ? "pointer" : "default",
        transition: "border-color 0.15s, background 0.15s",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ fontSize: "13px", fontWeight: 500, color: "#6B7280" }}>{label}</span>
        {Icon && (
          <span style={{ color: t.color, background: t.bg, borderRadius: "6px", padding: "6px", display: "flex" }}>
            <Icon size={16} />
          </span>
        )}
      </div>
      <div style={{ fontSize: "28px", fontWeight: 700, color: "#111827", lineHeight: 1.2 }}>{value}</div>
    </div>
  );
}

export function StatusBadge({ status }) {
  const t = TINTS[STATUS_TINT[status]] || TINTS.gray;
  return (
    <span
      style={{
        display: "inline-block",
        padding: "3px 12px",
        borderRadius: "9999px",
        fontSize: "12px",
        fontWeight: 600,
        background: t.bg,
        color: t.color,
        border: `1px solid ${t.border}`,
      }}
    >
      {status}
    </span>
  );
}
